// src/FullPageChat.jsx
// Dedicated full-screen chat experience for paid users

import { useState, useRef, useEffect, useCallback } from "react";
import { chatHeaders } from "./supabaseClient";
import { linkify } from "./linkify";

const IreneAvatar = ({ size = 32 }) => (
  <div style={{
    width: `${size}px`, height: `${size}px`, borderRadius: "50%",
    background: "linear-gradient(135deg, var(--accent), var(--accent-light))",
    display: "flex", alignItems: "center", justifyContent: "center",
    color: "#fff", fontSize: `${Math.round(size * 0.44)}px`,
    fontFamily: "'DM Serif Display', serif", fontWeight: "400",
    flexShrink: 0,
  }} aria-hidden="true">I</div>
);

const ThinkingDots = () => (
  <div style={{ display: "flex", alignItems: "center", gap: "5px", padding: "8px 0" }}
    aria-hidden="true">
    {[0, 1, 2].map((i) => (
      <div key={i} style={{
        width: "7px", height: "7px", borderRadius: "50%",
        backgroundColor: "var(--ink-muted)",
        animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite`,
      }} />
    ))}
  </div>
);

// Parse a stored message that may contain a leading attachment line
// Format: "filename.pdf\n<user text>" or just "filename.pdf"
const parseMessageContent = (content) => {
  if (!content) return { fileName: null, text: "" };
  if (!content.startsWith("\u{1F4CE} ")) return { fileName: null, text: content };
  const newlineIdx = content.indexOf("\n");
  if (newlineIdx === -1) return { fileName: content.slice(3), text: "" };
  return { fileName: content.slice(3, newlineIdx), text: content.slice(newlineIdx + 1) };
};

// Small pill shown inside a user bubble when a file was attached
const AttachmentPill = ({ name }) => (
  <div style={{
    display: "inline-flex", alignItems: "center", gap: "5px",
    backgroundColor: "rgba(255,255,255,0.18)",
    borderRadius: "6px", padding: "4px 8px",
    fontSize: "12px", marginBottom: "6px",
    maxWidth: "100%", overflow: "hidden",
  }}>
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
    </svg>
    <span style={{
      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
    }}>{name}</span>
  </div>
);

const MAX_FILE_SIZE = 4 * 1024 * 1024; // 4MB — Netlify Functions body limit is 6MB
const ALLOWED_TYPES = ["image/jpeg", "image/png", "application/pdf"];

const FullPageChat = ({
  user,
  conversationHook,
  sidebarOpen,
  onToggleSidebar,
  onAccount,
  onHome,
  onAbout,
  onCompare,
  onBlog,
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [started, setStarted] = useState(false);
  const [pendingFile, setPendingFile] = useState(null); // { name, mediaType, base64, fileType }
  const [fileError, setFileError] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const messagesContainerRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  // Prevents the activeConversationId useEffect from reloading messages when
  // the ID was just set by createConversation (during sendMessage), not by the
  // user selecting an existing conversation from the sidebar.
  const skipNextConvoLoadRef = useRef(false);

  const {
    activeConversationId,
    setActiveConversationId,
    createConversation,
    loadMessages,
    saveMessage,
    updateLocalTitle,
  } = conversationHook;

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  useEffect(() => {
    if (started && inputRef.current) inputRef.current.focus();
  }, [started]);

  useEffect(() => {
    if (!activeConversationId) return;
    // Skip when the ID was just set by createConversation inside sendMessage —
    // messages are already in local state and loading from DB would race/duplicate.
    if (skipNextConvoLoadRef.current) {
      skipNextConvoLoadRef.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      const loaded = await loadMessages(activeConversationId);
      if (!cancelled && loaded.length > 0) {
        setMessages(loaded);
        setStarted(true);
      }
    })();
    return () => { cancelled = true; };
  }, [activeConversationId, loadMessages]);

  const handleNewConversation = useCallback(() => {
    setMessages([]);
    setStarted(false);
    setInput("");
    setPendingFile(null);
    setFileError(null);
    if (setActiveConversationId) setActiveConversationId(null);
  }, [setActiveConversationId]);

  useEffect(() => {
    if (conversationHook) {
      conversationHook._handleNewConversation = handleNewConversation;
    }
  }, [conversationHook, handleNewConversation]);

  // Handle file selection from the hidden input
  const handleFileSelect = useCallback((e) => {
    const file = e.target.files[0];
    e.target.value = ""; // reset so same file can be re-selected
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      setFileError("Only PDF, JPG, and PNG files are supported.");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setFileError("File must be under 4MB.");
      return;
    }

    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target.result.split(",")[1];
      setPendingFile({
        name: file.name,
        mediaType: file.type,
        base64,
        fileType: file.type === "application/pdf" ? "document" : "image",
      });
      setFileError(null);
      if (!started) setStarted(true);
      if (inputRef.current) inputRef.current.focus();
    };
    reader.onerror = () => setFileError("Couldn't read that file. Please try again.");
    reader.readAsDataURL(file);
  }, [started]);

  const sendMessage = useCallback(async (userMessage, isInitial = false) => {
    const fileToSend = isInitial ? null : pendingFile;

    // Build the display string stored in local state and Supabase
    // Format: "📎 filename.pdf\nuser text" — parsed back out for rendering
    const displayContent = fileToSend
      ? (`\u{1F4CE} ${fileToSend.name}` + (userMessage ? `\n${userMessage}` : ""))
      : userMessage;

    const newMessages = isInitial
      ? []
      : [...messages, { role: "user", content: displayContent }];

    if (!isInitial) {
      setMessages(newMessages);
      setInput("");
      setPendingFile(null);
      setFileError(null);
    }
    setIsLoading(true);

    let convoId = activeConversationId;
    if (user && createConversation) {
      if (!convoId) {
        skipNextConvoLoadRef.current = true; // createConversation will change activeConversationId;
        // tell the useEffect not to reload — messages are already in local state
        const convo = await createConversation(user.id);
        if (convo) convoId = convo.id;
      }
      if (!isInitial && convoId) {
        await saveMessage(convoId, "user", displayContent);
        if (updateLocalTitle) updateLocalTitle(convoId, userMessage || fileToSend?.name || "Uploaded document");
      }
    }

    try {
      // API messages use plain text content (base64 never stored in history)
      const apiMessages = isInitial
        ? [{ role: "user", content: "Hi, I need help with my medical bills." }]
        : newMessages.map((m) => {
            const { text } = parseMessageContent(m.content);
            return { role: m.role, content: text || m.content };
          });

      const requestBody = { messages: apiMessages };
      if (fileToSend) {
        // Replace last message text with just the user's typed text (attachment goes separately)
        const last = apiMessages[apiMessages.length - 1];
        if (last) last.content = userMessage || "";
        requestBody.attachment = {
          base64: fileToSend.base64,
          mediaType: fileToSend.mediaType,
          fileType: fileToSend.fileType,
        };
      }

      const response = await fetch("/api/chat", {
        method: "POST",
        headers: await chatHeaders(),
        body: JSON.stringify(requestBody),
      });
      const data = await response.json();
      if (!response.ok) {
        const errMsg = data.error || "I'm having a little trouble connecting right now. Please try again in a moment.";
        setMessages((prev) => [...prev, { role: "assistant", content: errMsg }]);
        return;
      }
      const assistantContent = data.response || "I'm sorry, I had trouble responding. Could you try again?";
      setMessages((prev) => [...prev, { role: "assistant", content: assistantContent }]);

      if (convoId && saveMessage) {
        await saveMessage(convoId, "assistant", assistantContent);
      }
    } catch {
      setMessages((prev) => [...prev, {
        role: "assistant",
        content: "I'm having a little trouble connecting right now. Please try again in a moment.",
      }]);
    } finally {
      setIsLoading(false);
    }
  }, [messages, pendingFile, user, activeConversationId, createConversation, saveMessage, updateLocalTitle]);

  const handleStart = () => { setStarted(true); sendMessage("", true); };

  const canSend = !isLoading && (input.trim() || pendingFile);

  const firstName = user?.user_metadata?.full_name?.split(" ")[0] || "";
  const avatarUrl = user?.user_metadata?.avatar_url;

  return (
    <div style={{
      height: "100vh", display: "flex", flexDirection: "column",
      backgroundColor: "var(--surface)",
    }}>
      {/* Top bar */}
      <header className="chat-header" style={{
        padding: "18px 24px",
        borderBottom: "1px solid var(--border-light)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        backgroundColor: "rgba(250, 250, 248, 0.92)",
        backdropFilter: "blur(16px)",
        flexShrink: 0,
      }}>
        <div className="chat-header-left" style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <button
            onClick={onToggleSidebar}
            aria-label="Toggle conversation history"
            style={{
              background: "none", border: "none", cursor: "pointer",
              padding: "6px", borderRadius: "6px", display: "flex",
              alignItems: "center", justifyContent: "center",
              color: "var(--ink-soft)", transition: "all 0.15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--surface-warm)"; e.currentTarget.style.color = "var(--ink)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "var(--ink-soft)"; }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
              <rect x="1" y="1" width="5" height="16" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
              <rect x="8" y="1" width="9" height="16" rx="1.5" stroke="currentColor" strokeWidth="1.5"/>
            </svg>
          </button>

          <button
            onClick={onHome}
            style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: "20px", color: "var(--ink)", letterSpacing: "-0.02em",
              background: "none", border: "none", cursor: "pointer", padding: 0,
              whiteSpace: "nowrap",
            }}
            aria-label="Ask Irene home"
          ><span className="hide-on-mobile">Ask </span>Irene</button>

          <button
            className="new-btn"
            onClick={handleNewConversation}
            aria-label="New conversation"
            style={{
              background: "none", border: "1px solid var(--border)",
              borderRadius: "6px", padding: "5px 12px",
              cursor: "pointer", fontFamily: "inherit",
              fontSize: "13px", color: "var(--ink-soft)",
              display: "flex", alignItems: "center", gap: "4px",
              transition: "all 0.15s",
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--ink-muted)"; e.currentTarget.style.color = "var(--ink)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--border)"; e.currentTarget.style.color = "var(--ink-soft)"; }}
          >
            <span style={{ fontSize: "15px", lineHeight: 1 }}>+</span><span className="hide-on-mobile"> New</span>
          </button>
        </div>

        <div className="chat-header-right" style={{ display: "flex", alignItems: "center", gap: "24px" }}>
          <div className="nav-links" role="navigation">
            {[["Pricing", onCompare], ["Our story", onAbout], ["Blog", onBlog]].map(([label, handler]) => (
              <button key={label} onClick={handler} style={{
                background: "none", border: "none", cursor: "pointer",
                fontSize: "14px", fontWeight: "400", color: "var(--ink-soft)",
                fontFamily: "inherit", transition: "color 0.2s", padding: "4px 0",
              }}
                onMouseEnter={(e) => e.target.style.color = "var(--ink)"}
                onMouseLeave={(e) => e.target.style.color = "var(--ink-soft)"}
              >{label}</button>
            ))}
          </div>

          <button
            onClick={onAccount}
            style={{
              display: "flex", alignItems: "center", gap: "8px",
              background: "none", border: "1px solid var(--border)",
              borderRadius: "24px", padding: "6px 14px 6px 6px",
              cursor: "pointer", fontFamily: "inherit", transition: "border-color 0.2s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = "var(--ink-muted)"}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = "var(--border)"}
            aria-label="Your account"
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="" style={{ width: "26px", height: "26px", borderRadius: "50%" }} referrerPolicy="no-referrer" />
            ) : (
              <div style={{
                width: "26px", height: "26px", borderRadius: "50%",
                backgroundColor: "var(--accent)", color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "12px", fontWeight: "600",
              }}>
                {(firstName || "U").charAt(0).toUpperCase()}
              </div>
            )}
            <span className="hide-on-mobile" style={{ fontSize: "13px", color: "var(--ink-soft)" }}>
              {firstName || "Account"}
            </span>
          </button>

          {/* Hamburger — mobile only */}
          <button
            className="hamburger-btn"
            onClick={() => setMobileNavOpen((o) => !o)}
            aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileNavOpen}
          >
            {mobileNavOpen ? (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                <line x1="5" y1="5" x2="15" y2="15"/><line x1="15" y1="5" x2="5" y2="15"/>
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
                <line x1="3" y1="6" x2="17" y2="6"/><line x1="3" y1="10" x2="17" y2="10"/><line x1="3" y1="14" x2="17" y2="14"/>
              </svg>
            )}
          </button>
        </div>
      </header>

      {/* Mobile nav panel */}
      {mobileNavOpen && (
        <div className="mobile-nav" style={{
          borderBottom: "1px solid var(--border-light)",
          backgroundColor: "rgba(250, 250, 248, 0.97)",
          backdropFilter: "blur(16px)",
          padding: "4px 24px 12px",
          flexShrink: 0,
        }}>
          {[["Pricing", onCompare], ["Our story", onAbout], ["Blog", onBlog], ["View site", onHome]].map(([label, handler]) => (
            <button
              key={label}
              className="mobile-nav-link"
              onClick={() => { handler(); setMobileNavOpen(false); }}
            >{label}</button>
          ))}
        </div>
      )}

      {/* Chat area */}
      <main id="main" style={{
        flex: 1, display: "flex", flexDirection: "column",
        maxWidth: "800px", width: "100%", margin: "0 auto",
        overflow: "hidden",
      }}>
        {!started ? (
          <div style={{
            flex: 1, display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center",
            padding: "40px 24px", textAlign: "center",
          }}>
            <IreneAvatar size={56} />
            <h1 style={{
              fontFamily: "'DM Serif Display', serif",
              fontSize: "clamp(24px, 4vw, 32px)", fontWeight: "400",
              color: "var(--ink)", letterSpacing: "-0.02em",
              marginTop: "20px", marginBottom: "8px",
            }}>
              {firstName ? `Hi ${firstName}.` : "Hi there."}
            </h1>
            <p style={{
              fontSize: "16px", color: "var(--ink-soft)",
              maxWidth: "400px", lineHeight: 1.6, marginBottom: "32px",
            }}>
              I'm Irene, an AI assistant for medical bills. What can I help you with today?
            </p>
            <button onClick={handleStart} style={{
              padding: "14px 32px", fontSize: "15px", fontWeight: "500",
              backgroundColor: "var(--accent)", color: "#fff",
              border: "none", borderRadius: "24px", cursor: "pointer",
              transition: "all 0.2s ease",
            }}
              onMouseEnter={(e) => { e.target.style.transform = "translateY(-1px)"; e.target.style.boxShadow = "0 4px 12px rgba(45,90,61,0.25)"; }}
              onMouseLeave={(e) => { e.target.style.transform = "translateY(0)"; e.target.style.boxShadow = "none"; }}
            >
              Start a conversation
            </button>

            <div style={{
              display: "flex", flexWrap: "wrap", gap: "8px",
              justifyContent: "center", marginTop: "24px", maxWidth: "480px",
            }}>
              {[
                "Help me understand my EOB",
                "I got a surprise bill",
                "Find financial assistance",
                "Should I appeal a denied claim?",
              ].map((prompt) => (
                <button key={prompt}
                  onClick={() => { setStarted(true); sendMessage(prompt); }}
                  style={{
                    padding: "8px 14px", fontSize: "13px",
                    backgroundColor: "var(--surface-warm)", color: "var(--ink-soft)",
                    border: "1px solid var(--border-light)",
                    borderRadius: "20px", cursor: "pointer",
                    fontFamily: "inherit", transition: "all 0.15s",
                  }}
                  onMouseEnter={(e) => { e.target.style.borderColor = "var(--accent)"; e.target.style.color = "var(--accent)"; }}
                  onMouseLeave={(e) => { e.target.style.borderColor = "var(--border-light)"; e.target.style.color = "var(--ink-soft)"; }}
                >
                  {prompt}
                </button>
              ))}
            </div>

            {/* Upload prompt on welcome screen */}
            <p style={{
              marginTop: "20px", fontSize: "13px", color: "var(--ink-muted)",
            }}>
              Or{" "}
              <button
                onClick={() => fileInputRef.current?.click()}
                style={{
                  background: "none", border: "none", padding: 0,
                  fontSize: "13px", color: "var(--accent)", cursor: "pointer",
                  fontFamily: "inherit", textDecoration: "underline",
                  textUnderlineOffset: "2px",
                }}
              >
                upload a bill or EOB
              </button>
              {" "}for a line-by-line review.
            </p>
          </div>
        ) : (
          <>
          <div
            role="status"
            aria-live="polite"
            className="sr-only"
          >
            {isLoading ? "Irene is writing a response" : ""}
          </div>
          <div
            ref={messagesContainerRef}
            style={{
              flex: 1, overflowY: "auto",
              padding: "24px 16px",
              display: "flex", flexDirection: "column", gap: "16px",
            }}
            role="log" aria-live="polite"
            aria-relevant="additions text"
            aria-label="Conversation with Irene"
          >
            {messages.map((msg, i) => {
              const { fileName, text } = parseMessageContent(msg.content);
              return (
                <div key={i} style={{
                  display: "flex",
                  flexDirection: msg.role === "user" ? "row-reverse" : "row",
                  alignItems: "flex-start",
                  gap: "10px", maxWidth: "100%",
                }}>
                  {msg.role === "assistant" && <IreneAvatar />}
                  <div style={{
                    maxWidth: "72%",
                    padding: "12px 16px",
                    borderRadius: msg.role === "user" ? "18px 18px 6px 18px" : "18px 18px 18px 6px",
                    backgroundColor: msg.role === "user" ? "var(--accent)" : "var(--surface-warm)",
                    color: msg.role === "user" ? "#fff" : "var(--ink)",
                    border: msg.role === "assistant" ? "1px solid var(--border-light)" : "none",
                    fontSize: "15px", lineHeight: 1.65,
                    whiteSpace: "pre-wrap",
                  }}>
                    {fileName && <AttachmentPill name={fileName} />}
                    {text && <div>{msg.role === "assistant" ? linkify(text) : text}</div>}
                  </div>
                </div>
              );
            })}
            {isLoading && (
              <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                <IreneAvatar />
                <div style={{
                  padding: "12px 16px",
                  borderRadius: "18px 18px 18px 6px",
                  backgroundColor: "var(--surface-warm)",
                  border: "1px solid var(--border-light)",
                }}>
                  <ThinkingDots />
                </div>
              </div>
            )}
          </div>
          </>
        )}

        {/* Hidden file input — must be outside the started conditional so the ref
            is always mounted (e.g. the welcome-screen upload link needs to click it) */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={handleFileSelect}
          style={{ display: "none" }}
          aria-hidden="true"
        />

        {/* Input bar — always visible once started, or when a file is staged */}
        {(started || pendingFile) && (
          <div style={{ padding: "12px 16px 20px", flexShrink: 0 }}>

            {/* File error */}
            {fileError && (
              <div
                role="alert"
                style={{
                  fontSize: "12px", color: "#c0392b",
                  marginBottom: "6px", paddingLeft: "4px",
                }}
              >
                {fileError}
              </div>
            )}

            {/* Staged file preview */}
            {pendingFile && (
              <div style={{
                display: "flex", alignItems: "center", gap: "8px",
                backgroundColor: "var(--surface-warm)",
                border: "1px solid var(--border-light)",
                borderRadius: "10px", padding: "7px 12px",
                marginBottom: "6px", fontSize: "13px", color: "var(--ink-soft)",
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--accent)"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
                </svg>
                <span style={{
                  flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  color: "var(--ink)",
                }}>
                  {pendingFile.name}
                </span>
                <button
                  onClick={() => { setPendingFile(null); setFileError(null); }}
                  aria-label="Remove file"
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    color: "var(--ink-muted)", padding: "0 2px", fontSize: "16px",
                    lineHeight: 1, flexShrink: 0,
                  }}
                >×</button>
              </div>
            )}

            <div style={{
              display: "flex", gap: "8px", alignItems: "center",
              backgroundColor: "#fff",
              border: "1px solid var(--border)",
              borderRadius: "24px",
              padding: "4px 4px 4px 8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
              transition: "border-color 0.2s",
            }}
              onFocus={(e) => e.currentTarget.style.borderColor = "var(--accent)"}
              onBlur={(e) => e.currentTarget.style.borderColor = "var(--border)"}
            >
              {/* Paperclip button */}
              <button
                onClick={() => { setFileError(null); fileInputRef.current?.click(); }}
                disabled={isLoading}
                aria-label="Attach a bill or EOB (PDF, JPG, PNG — max 4MB)"
                title="Attach bill or EOB"
                style={{
                  background: "none", border: "none", cursor: isLoading ? "not-allowed" : "pointer",
                  padding: "6px 6px 4px",
                  color: pendingFile ? "var(--accent)" : "var(--ink-muted)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  transition: "color 0.15s", flexShrink: 0,
                  borderRadius: "50%",
                }}
                onMouseEnter={(e) => { if (!isLoading && !pendingFile) e.currentTarget.style.color = "var(--ink-soft)"; }}
                onMouseLeave={(e) => { if (!pendingFile) e.currentTarget.style.color = "var(--ink-muted)"; }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/>
                </svg>
              </button>

              <label htmlFor="chat-input-fp" style={{ position: "absolute", left: "-9999px" }}>
                Type your message to Irene
              </label>
              <input
                id="chat-input-fp"
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && canSend) sendMessage(input.trim());
                }}
                placeholder={pendingFile ? "Add a message or just hit send…" : "Message Irene…"}
                disabled={isLoading}
                style={{
                  flex: 1, padding: "10px 0", fontSize: "15px",
                  border: "none", backgroundColor: "transparent",
                  color: "var(--ink)", fontFamily: "inherit",
                }}
              />
              <button
                onClick={() => { if (canSend) sendMessage(input.trim()); }}
                disabled={!canSend}
                aria-label="Send message"
                style={{
                  width: "36px", height: "36px",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  backgroundColor: canSend ? "var(--accent)" : "var(--border)",
                  color: "#fff",
                  border: "none", borderRadius: "50%",
                  cursor: canSend ? "pointer" : "not-allowed",
                  transition: "all 0.2s ease", fontSize: "16px", flexShrink: 0,
                }}
              >↑</button>
            </div>
            <p style={{
              fontSize: "11px", color: "var(--ink-faint)", marginTop: "8px",
              textAlign: "center",
            }}>
              Irene provides general guidance — not legal, financial, or medical advice.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default FullPageChat;
